import React, { useState } from "react";
import { styled } from "styled-components";

import { ClosedChatFrame } from "./ClosedChatFrame";
import { textColorForBackground } from "./textColor";
import {
  AnnouncementColors,
  AnnouncementPlacement,
  AnnouncementPosition,
} from "./types";

const Card = styled.div(({ theme }) => ({
  width: 240,
  overflow: "hidden",
  background: theme.colors.white,
  borderRadius: theme.radii[2],
  boxShadow: theme.shadows.surfaceZ1Light,
}));

const Message = styled.div<{ $background: string; $color: string }>(
  ({ $background, $color }) => ({
    padding: "12px 14px",
    background: $background,
    color: $color,
    fontSize: 14,
  }),
);

const Composer = styled.form(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: "8px 10px 8px 12px",
  background: theme.colors.white,
  borderTop: `1px solid ${theme.colors.grayMed}`,
}));

const Field = styled.input(({ theme }) => ({
  flex: 1,
  minWidth: 0,
  margin: 0,
  marginRight: 8,
  padding: 0,
  border: 0,
  background: "transparent",
  color: theme.colors.black,
  fontSize: 14,
  fontFamily: theme.fonts.default,

  "&:focus": {
    outline: "none",
  },

  "&::placeholder": {
    color: theme.colors.grayDark,
  },
}));

const Send = styled.button<{ $color: string; $ready: boolean }>(
  ({ $color, $ready }) => ({
    margin: 0,
    padding: "4px 0 4px 4px",
    border: 0,
    background: "transparent",
    color: $color,
    fontSize: 14,
    fontWeight: 400,
    opacity: $ready ? 1 : 0.45,
    cursor: $ready ? "pointer" : "not-allowed",
  }),
);

export interface PromptToChatProps extends AnnouncementColors {
  message: string;
  placement: AnnouncementPlacement;
  position?: AnnouncementPosition;
  onSend?: (message: string) => void;
  onDismiss?: () => void;
}

export const PromptToChat: React.FC<PromptToChatProps> = ({
  message,
  placement,
  position = "right",
  primaryColor,
  onSend,
  onDismiss,
}) => {
  const [draft, setDraft] = useState("");
  const ready = draft.trim().length > 0;

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    const text = draft.trim();

    if (!text) {
      return;
    }

    if (onSend) {
      onSend(text);
    }

    setDraft("");
  };

  return (
    <ClosedChatFrame
      placement={placement}
      position={position}
      onDismiss={onDismiss}
      label="Prompt to chat"
    >
      <Card>
        <Message
          $background={primaryColor}
          $color={textColorForBackground(primaryColor)}
        >
          {message}
        </Message>
        <Composer onSubmit={submit}>
          <Field
            type="text"
            value={draft}
            placeholder="Ask something..."
            aria-label="Message"
            onChange={(event) => {
              setDraft(event.target.value);
            }}
          />
          <Send
            type="submit"
            $color={primaryColor}
            $ready={ready}
            disabled={!ready}
          >
            Send
          </Send>
        </Composer>
      </Card>
    </ClosedChatFrame>
  );
};
